import type { PlacedObject, PropMod } from '../model/format.ts';
import type { PrefabDef } from '../model/gamedata.ts';
import { applyPropertyPath, num } from '../model/unityProps.ts';
import { IDENTITY, compose, localXform, mul, type Xform } from '../model/xform.ts';
import { gameData, sortingLayerNameFromId, spriteFromRef, type SpriteRef } from './gameData.ts';

export interface InstNode {
  id: string;
  type: string;
  data: any;
  go: InstGo;
}

export interface InstGo {
  id: string;
  name: string;
  parent: InstGo | null;
  children: InstGo[];
  activeSelf: boolean;
  active: boolean;
  comps: InstNode[];
  transform: InstNode | null;
  local: Xform;
  world: Xform;
}

export interface PrefabInstance {
  def: PrefabDef;
  gos: InstGo[];
  nodes: Map<string, InstNode>;
  root: InstGo;
}

/** Turn a PropMod reference string into the object stored in component data. */
export function refObject(r: string): any {
  if (r.startsWith('asset:')) {
    const [, guid, fileID] = r.split(':');
    return { fileID, guid };
  }
  return { fileID: 'entity', entity: r };
}

export function resolveNodeId(def: PrefabDef, id: string): string {
  return def.alias[id] ?? id;
}

/**
 * Instantiate a prefab definition with Unity property modifications applied, computing
 * hierarchy, activity and transforms. `pose` overrides the root transform (placed objects).
 */
export function instantiatePrefab(guid: string, mods: PropMod[] = [], pose?: { x: number; y: number; rotation: number; scale: [number, number] }): PrefabInstance | null {
  const def = gameData().prefabs[guid];
  if (!def) return null;
  const nodes = new Map<string, InstNode>();
  const data = new Map<string, any>();
  for (const [id, n] of Object.entries(def.nodes)) data.set(id, structuredClone(n.data));
  for (const m of mods) {
    const target = data.get(resolveNodeId(def, m.t));
    if (!target) continue;
    applyPropertyPath(target, m.p, m.v ?? '', m.r ? refObject(m.r) : undefined);
  }
  const gos: InstGo[] = def.gos.map((g) => ({
    id: g.id,
    name: String(data.get(g.id)?.m_Name ?? g.name),
    parent: null,
    children: [],
    activeSelf: String(data.get(g.id)?.m_IsActive ?? '1') !== '0',
    active: true,
    comps: [],
    transform: null,
    local: IDENTITY,
    world: IDENTITY,
  }));
  def.gos.forEach((g, i) => {
    const go = gos[i];
    if (g.parent >= 0) {
      go.parent = gos[g.parent];
      gos[g.parent].children.push(go);
    }
    for (const cid of g.comps) {
      const n = def.nodes[cid];
      const node: InstNode = { id: cid, type: n.type, data: data.get(cid), go };
      nodes.set(cid, node);
      go.comps.push(node);
      if (n.type === 'Transform' || n.type === 'RectTransform') go.transform = node;
    }
  });
  for (const go of gos) {
    go.local = localXform(go.transform?.data);
    go.active = go.activeSelf && (go.parent ? go.parent.active : true);
    go.world = go.parent ? mul(go.parent.world, go.local) : go.local;
  }
  const root = gos[0];
  if (pose) setRootPose({ def, gos, nodes, root }, pose);
  return { def, gos, nodes, root };
}

export function setRootPose(inst: PrefabInstance, pose: { x: number; y: number; rotation: number; scale: [number, number] }) {
  inst.root.local = compose(pose.x, pose.y, pose.rotation, pose.scale[0], pose.scale[1]);
  for (const go of inst.gos) go.world = go.parent ? mul(go.parent.world, go.local) : go.local;
}

export function instantiateObject(o: PlacedObject): PrefabInstance | null {
  if (!o.prefab) return null;
  return instantiatePrefab(o.prefab, o.mods, o);
}

export function findComponent(inst: PrefabInstance, type: string): InstNode | undefined {
  for (const go of inst.gos) for (const c of go.comps) if (c.type === type) return c;
  return undefined;
}

export function componentsOf(inst: PrefabInstance, type: string): InstNode[] {
  const out: InstNode[] = [];
  for (const go of inst.gos) for (const c of go.comps) if (c.type === type) out.push(c);
  return out;
}

export function enabled(n: InstNode | undefined): boolean {
  return !!n && String(n.data?.m_Enabled ?? '1') !== '0';
}

/** Nearest SpriteMask on this object or under an ancestor (each volleyball masks its own seams). */
function spriteMaskFor(start: InstGo): SpriteMaskDraw | null {
  let cur: InstGo | null = start;
  while (cur) {
    const hit = spriteMaskUnder(cur);
    if (hit) return hit;
    cur = cur.parent;
  }
  return null;
}

function spriteMaskUnder(go: InstGo): SpriteMaskDraw | null {
  if (!go.active) return null;
  for (const c of go.comps) {
    if (c.type !== 'SpriteMask' || !enabled(c)) continue;
    const sprite = spriteFromRef(c.data.m_Sprite);
    if (sprite) return { sprite, world: go.world };
  }
  for (const ch of go.children) {
    const hit = spriteMaskUnder(ch);
    if (hit) return hit;
  }
  return null;
}

// ---------- drawables ----------

export interface SpriteMaskDraw {
  sprite: SpriteRef;
  world: Xform;
}

export interface SpriteDrawable {
  kind: 'sprite';
  sprite: SpriteRef;
  world: Xform;
  color: [number, number, number, number];
  flipX: boolean;
  flipY: boolean;
  sortingLayer: string;
  order: number;
  /** 0 simple, 1 sliced, 2 tiled */
  drawMode: number;
  size: [number, number];
  /** tiled mode: 0 continuous, 1 adaptive (stretch until the threshold, then add a tile) */
  tileMode?: number;
  tileThreshold?: number;
  /** SpriteRenderer.m_MaskInteraction: 0 none, 1 visible inside mask, 2 visible outside mask */
  maskInteraction?: number;
  /** Hierarchy SpriteMask this sprite is clipped by. */
  mask?: SpriteMaskDraw;
  go: InstGo;
}

export interface TilemapDrawable {
  kind: 'tilemap';
  tiles: number[];
  palette: { tex: string; sprite: string; tile?: string }[];
  world: Xform;
  color: [number, number, number, number];
  sortingLayer: string;
  order: number;
  go: InstGo;
  key: string;
}

export type Drawable = SpriteDrawable | TilemapDrawable;

export function colorOf(c: any, def: [number, number, number, number] = [1, 1, 1, 1]): [number, number, number, number] {
  if (!c) return def;
  return [num(c.r, 1), num(c.g, 1), num(c.b, 1), num(c.a, 1)];
}

/** Sprites and tilemaps an instance renders in its current state. */
export function instanceDrawables(inst: PrefabInstance, includeInactive = false): Drawable[] {
  const out: Drawable[] = [];
  for (const go of inst.gos) {
    if (!go.active && !includeInactive) continue;
    for (const c of go.comps) {
      if (c.type === 'SpriteRenderer' && enabled(c)) {
        const sprite = spriteFromRef(c.data.m_Sprite);
        if (!sprite) continue;
        const maskInteraction = num(c.data.m_MaskInteraction);
        const mask = maskInteraction === 1 || maskInteraction === 2 ? spriteMaskFor(go) ?? undefined : undefined;
        out.push({
          kind: 'sprite',
          sprite,
          world: go.world,
          color: colorOf(c.data.m_Color),
          flipX: String(c.data.m_FlipX) === '1',
          flipY: String(c.data.m_FlipY) === '1',
          sortingLayer: sortingLayerNameFromId(c.data.m_SortingLayerID),
          order: num(c.data.m_SortingOrder),
          drawMode: num(c.data.m_DrawMode),
          size: [num(c.data.m_Size?.x, 1), num(c.data.m_Size?.y, 1)],
          tileMode: num(c.data.m_SpriteTileMode),
          tileThreshold: num(c.data.m_AdaptiveModeThreshold, 0.5),
          maskInteraction,
          mask,
          go,
        });
      } else if (c.type === 'Tilemap') {
        const r = go.comps.find((x) => x.type === 'TilemapRenderer');
        if (r && !enabled(r)) continue;
        out.push({
          kind: 'tilemap',
          tiles: c.data.tiles ?? [],
          palette: c.data.palette ?? [],
          world: go.world,
          color: colorOf(c.data.m_Color),
          sortingLayer: sortingLayerNameFromId(r?.data?.m_SortingLayerID),
          order: num(r?.data?.m_SortingOrder),
          go,
          key: `${inst.def.guid}:${c.id}`,
        });
      }
    }
  }
  return out;
}

/** Approximate world-space bounds of an instance (for picking). */
export function instanceBounds(inst: PrefabInstance): [number, number, number, number] | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const add = (m: Xform, lx: number, ly: number) => {
    const x = m.a * lx + m.c * ly + m.tx, y = m.b * lx + m.d * ly + m.ty;
    x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  };
  for (const d of instanceDrawables(inst)) {
    if (d.kind === 'sprite') {
      const [w, h] = spriteWorldSize(d);
      const px = d.drawMode ? 0.5 : d.sprite.def.p[0], py = d.drawMode ? 0.5 : d.sprite.def.p[1];
      add(d.world, -px * w, -py * h);
      add(d.world, (1 - px) * w, (1 - py) * h);
      add(d.world, -px * w, (1 - py) * h);
      add(d.world, (1 - px) * w, -py * h);
    } else {
      for (let i = 0; i < d.tiles.length; i += 4) {
        add(d.world, d.tiles[i], d.tiles[i + 1]);
        add(d.world, d.tiles[i] + 1, d.tiles[i + 1] + 1);
      }
    }
  }
  for (const go of inst.gos) {
    if (!go.active) continue;
    for (const c of go.comps) {
      if (c.type === 'BoxCollider2D') {
        const sx = num(c.data.m_Size?.x, 1), sy = num(c.data.m_Size?.y, 1), ox = num(c.data.m_Offset?.x), oy = num(c.data.m_Offset?.y);
        add(go.world, ox - sx / 2, oy - sy / 2);
        add(go.world, ox + sx / 2, oy + sy / 2);
      } else if (c.type === 'CircleCollider2D') {
        const r = num(c.data.m_Radius, 0.5), ox = num(c.data.m_Offset?.x), oy = num(c.data.m_Offset?.y);
        add(go.world, ox - r, oy - r);
        add(go.world, ox + r, oy + r);
      }
    }
  }
  if (!Number.isFinite(x0)) {
    add(inst.root.world, -0.5, -0.5);
    add(inst.root.world, 0.5, 0.5);
  }
  return [x0, y0, x1, y1];
}

export function spriteWorldSize(d: SpriteDrawable): [number, number] {
  if (d.drawMode) return d.size;
  return [d.sprite.def.r[2] / d.sprite.texDef.ppu, d.sprite.def.r[3] / d.sprite.texDef.ppu];
}
